import { type RefObject, useCallback, useRef, useState } from 'react';

import { remToPx } from '../../helpers/css-units';
import { useIsomorphicLayoutEffect, useResizeObserver, useScrollListener } from '../../hooks';

import type { OverlayVariant } from './types';

type Pixel = number;
type Rem = string;
type HTMLElementRef = RefObject<HTMLElement | null>;

const getOffsetTopOfSide = (key: 'top' | 'bottom') => (element: HTMLElement) =>
  window.scrollY + element.getBoundingClientRect()[key];

const getOffsetLeftOfSide = (key: 'left' | 'right') => (element: HTMLElement) =>
  window.scrollX + element.getBoundingClientRect()[key];

const getAlignLeftPositionX = ({ overlay, target, documentWidth }: Omit<GetPositionXParams, 'variant'>) => {
  const targetLeft = getOffsetLeftOfSide('left')(target);
  const freeWidth = documentWidth - targetLeft;
  const shouldMoveToTheLeft = overlay.clientWidth > freeWidth;
  return shouldMoveToTheLeft ? targetLeft - (overlay.clientWidth - freeWidth) : targetLeft;
};

const getCenteredPositionX = ({
  overlay,
  target,
  documentWidth,
  overlaySpaceToTarget,
}: Omit<GetPositionXParams, 'variant'>) => {
  const targetLeft = getOffsetLeftOfSide('left')(target);
  const targetWidth = target.clientWidth;
  const overlayWidth = overlay.clientWidth;

  // Try to center the overlay on the target
  const centeredPosition = targetLeft + (targetWidth - overlayWidth) / 2;

  // Ensure overlay doesn't go off-screen
  const margin = remToPx(overlaySpaceToTarget);
  const minAllowed = margin;
  const maxAllowed = documentWidth - margin - overlayWidth;

  if (maxAllowed < minAllowed) {
    const minFallback = 0;
    const maxFallback = documentWidth - overlayWidth;
    return Math.max(minFallback, Math.min(centeredPosition, maxFallback));
  }

  return Math.max(minAllowed, Math.min(centeredPosition, maxAllowed));
};

type GetPositionXParams = {
  overlay: HTMLElement;
  target: HTMLElement;
  variant: OverlayVariant;
  documentWidth: Pixel;
  overlaySpaceToTarget: Rem;
};
const getOpenedPositionX = ({
  overlay,
  target,
  variant,
  documentWidth,
  overlaySpaceToTarget,
}: GetPositionXParams): Pixel => {
  switch (variant) {
    case 'align-left':
      return getAlignLeftPositionX({ overlay, target, documentWidth, overlaySpaceToTarget });
    case 'align-center':
      return getCenteredPositionX({ overlay, target, documentWidth, overlaySpaceToTarget });
    case 'fit-target':
      return getOffsetLeftOfSide('left')(target);
    case 'align-right':
      return getOffsetLeftOfSide('right')(target) - overlay.clientWidth;
    case 'fit-content':
      // For fit-content, center the overlay on the target but respect viewport bounds
      return getCenteredPositionX({ overlay, target, documentWidth, overlaySpaceToTarget });
  }
};

type UsePositionXParams = {
  variant: OverlayVariant;
  overlayRef: HTMLElementRef;
  targetRef: HTMLElementRef;
  isOpened: boolean;
  overlaySpaceToTarget: Rem;
};
export const usePositionX = ({
  overlayRef,
  targetRef,
  isOpened,
  variant,
  overlaySpaceToTarget,
}: UsePositionXParams): Pixel | null => {
  const [positionX, setPositionX] = useState<Pixel | null>(null);
  const setComputedPositionX = useCallback(() => {
    const target = targetRef.current;
    const overlay = overlayRef.current;
    if (!isOpened || !target || !overlay) {
      setPositionX(null);
      return;
    }
    setPositionX(
      getOpenedPositionX({
        overlay,
        target,
        variant,
        documentWidth: globalThis.document?.body?.clientWidth ?? 0,
        overlaySpaceToTarget,
      }),
    );
  }, [overlayRef, targetRef, isOpened, variant, overlaySpaceToTarget]);

  useIsomorphicLayoutEffect(setComputedPositionX, [setComputedPositionX]);
  useScrollListener({ elementRef: targetRef, listener: setComputedPositionX });
  useResizeObserver({
    elementRefs: [targetRef, overlayRef],
    observer: setComputedPositionX,
  });

  return positionX;
};

type GetPositionYParams = {
  overlay: HTMLElement;
  target: HTMLElement;
  documentHeight: Pixel;
  overlaySpaceToTarget: Rem;
};
const getOpenedPositionY = ({ overlay, target, documentHeight, overlaySpaceToTarget }: GetPositionYParams): Pixel => {
  const { scrollTop } = overlay;
  overlay.style.removeProperty('max-height');
  overlay.style.removeProperty('overflow-y');

  const spaceBetweenTargetAndOverlayInPx = remToPx(overlaySpaceToTarget);
  const overlayHeight = overlay.clientHeight;
  const neededHeight = overlayHeight + spaceBetweenTargetAndOverlayInPx;

  const targetTop = getOffsetTopOfSide('top')(target);
  const targetBottom = getOffsetTopOfSide('bottom')(target);

  const freeSpaceBelow = documentHeight - targetBottom;
  const freeSpaceAbove = targetTop;

  // Default
  if (neededHeight <= freeSpaceBelow) {
    return targetBottom + spaceBetweenTargetAndOverlayInPx;
  }

  // Not enough space below and enough space above
  if (neededHeight <= freeSpaceAbove) {
    return targetTop - spaceBetweenTargetAndOverlayInPx - overlayHeight;
  }

  // Neither side fits completely
  const availableHeight = Math.max(freeSpaceBelow, freeSpaceAbove);
  overlay.style.maxHeight = `${availableHeight - spaceBetweenTargetAndOverlayInPx}px`;
  overlay.style.overflowY = 'auto';
  // Measuring the overlay at its natural height clamped its scroll position to zero
  overlay.scrollTop = scrollTop;

  if (freeSpaceBelow >= freeSpaceAbove) {
    return targetBottom + spaceBetweenTargetAndOverlayInPx;
  } else {
    return targetTop - spaceBetweenTargetAndOverlayInPx - overlay.clientHeight;
  }
};

type UsePositionYParams = {
  overlayRef: HTMLElementRef;
  targetRef: HTMLElementRef;
  isOpened: boolean;
  overlaySpaceToTarget: Rem;
};
export const usePositionY = ({
  overlayRef,
  targetRef,
  isOpened,
  overlaySpaceToTarget,
}: UsePositionYParams): Pixel | null => {
  const [positionY, setPositionY] = useState<Pixel | null>(null);
  const setComputedPositionY = useCallback(() => {
    const overlay = overlayRef.current;
    const target = targetRef.current;
    if (!isOpened || !overlay || !target) {
      setPositionY(null);
      return;
    }
    setPositionY(
      getOpenedPositionY({
        target,
        overlay,
        overlaySpaceToTarget,
        documentHeight: globalThis.document?.body?.clientHeight ?? 0,
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overlayRef, targetRef, isOpened]);

  useIsomorphicLayoutEffect(setComputedPositionY, [setComputedPositionY]);
  useScrollListener({ elementRef: targetRef, listener: setComputedPositionY });
  useResizeObserver({
    elementRefs: [targetRef, overlayRef, useRef(globalThis.document?.body)],
    observer: setComputedPositionY,
  });

  return positionY;
};

type GetMaxWidthParams = {
  documentWidth: Pixel;
  variant: OverlayVariant;
  target: HTMLElement;
  overlaySpaceToTarget: Rem;
};
const getMaxWidth = ({ target, variant, documentWidth, overlaySpaceToTarget }: GetMaxWidthParams): Pixel => {
  const paddingFromScreenBorderInPx = remToPx(overlaySpaceToTarget);
  switch (variant) {
    case 'fit-target':
      return target.clientWidth;
    case 'align-right':
      return getOffsetLeftOfSide('right')(target) - paddingFromScreenBorderInPx;
    case 'align-left':
      return documentWidth - paddingFromScreenBorderInPx;
    case 'align-center':
      return Math.max(documentWidth - paddingFromScreenBorderInPx * 2, 0);
    case 'fit-content':
      // For fit-content, return the maximum possible width (full viewport minus padding)
      // This acts as a constraint, but the actual width will be determined by content
      return documentWidth - paddingFromScreenBorderInPx * 2;
  }
};

type UseMaxWidthParams = {
  variant: OverlayVariant;
  targetRef: HTMLElementRef;
  isOpened: boolean;
  overlaySpaceToTarget: Rem;
};
export const useMaxWidth = ({
  targetRef,
  isOpened,
  variant,
  overlaySpaceToTarget,
}: UseMaxWidthParams): Pixel | null => {
  const [maxWidth, setMaxWidth] = useState<Pixel | null>(null);
  const setComputedMaxWidth = useCallback(() => {
    const target = targetRef.current;
    if (!isOpened || !target) {
      setMaxWidth(null);
      return;
    }
    setMaxWidth(
      getMaxWidth({
        target,
        variant,
        overlaySpaceToTarget,
        documentWidth: globalThis.document?.body?.clientWidth ?? 0,
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetRef, isOpened, variant]);

  useIsomorphicLayoutEffect(setComputedMaxWidth, [setComputedMaxWidth]);
  useScrollListener({ elementRef: targetRef, listener: setComputedMaxWidth });
  useResizeObserver({
    elementRefs: [targetRef, useRef(globalThis.document?.body)],
    observer: setComputedMaxWidth,
  });

  return maxWidth;
};
